import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Buildings,
  Target,
  Eye,
  Handshake,
  Users,
  Lightbulb,
  Cpu,
  Code,
  ChartLineUp,
  Desktop,
  Fingerprint,
  GraduationCap,
  MapPin,
  Globe,
  ArrowRight,
  LinkedinLogo,
} from '@phosphor-icons/react';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';

const missionItems = [
  { icon: Lightbulb, text: 'Building Intelligent Technology' },
  { icon: Cpu, text: 'IT Architect Provider' },
  { icon: Code, text: 'Software APP Development' },
  { icon: ChartLineUp, text: 'Business Innovation Consultancy' },
  { icon: Desktop, text: 'Building Efficient Workspace System' },
  { icon: Fingerprint, text: 'Building Biometric Registration Systems' },
  { icon: GraduationCap, text: 'Leadership Building, Innovation & Workforce Knowledge Transfer' },
];

export default function AboutPage() {
  const { theme } = useTheme();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="about-page" className={`min-h-screen pt-24 ${theme === 'dark' ? 'bg-[#0A0A10]' : 'bg-[#FAFAFC]'}`}>
      {/* Hero Section */}
      <section className="py-10 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-3xl"
        >
          <span className="text-[#00C8EB] font-medium uppercase tracking-wider text-sm">
            About Us
          </span>
          <h1 className={`text-4xl md:text-5xl font-bold mt-3 mb-6 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Pioneering{' '}
            <span className="text-gradient">Intelligent Solutions</span>
          </h1>
          <p className={`text-lg ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Discover who we are, our mission, and how we're transforming businesses through technology.
          </p>
        </motion.div>
      </section>

      {/* Who We Are Section */}
      <section className="py-4 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-[#464B8B] to-[#5a5f9e] p-8 md:p-12"
        >
          <div className="absolute inset-0 opacity-10">
            <div 
              className="absolute inset-0"
              style={{
                backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            />
          </div>
          <div className="relative">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center">
                <Buildings size={28} weight="duotone" className="text-white" />
              </div>
              <h2 className="text-3xl font-bold text-white">
                Who We Are
              </h2>
            </div>
            <p className="text-lg leading-relaxed text-white/90 max-w-4xl">
              Atlas Softech is structured to function pragmatically as a Software Architect Solutions provider 
              and as a business strategist, with the aim of developing robust intelligent software solutions, 
              fostering innovation, and facilitating technological transformation. We build intelligent systems 
              for the governments, enterprises, hospitals and banks, fostering efficiency and sustainability.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Mission Section */}
      <section className="py-4 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-[#464B8B] to-[#00C8EB] p-8 md:p-12"
        >
          <div className="absolute inset-0 opacity-10">
            <div 
              className="absolute inset-0"
              style={{
                backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            />
          </div>
          <div className="relative">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center">
                <Target size={28} weight="duotone" className="text-white" />
              </div>
              <h2 className="text-3xl font-bold text-white">
                Mission
              </h2>
            </div>
            <p className="text-lg leading-relaxed text-white/90 max-w-4xl">
              To create and build secure intelligent automation systems that boost people's productivity and efficiency.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Vision Section */}
      <section className="py-4 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-[#00C8EB] to-[#464B8B] p-8 md:p-12"
        >
          <div className="absolute inset-0 opacity-10">
            <div 
              className="absolute inset-0"
              style={{
                backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            />
          </div>
          <div className="relative">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center">
                <Eye size={28} weight="duotone" className="text-white" />
              </div>
              <h2 className="text-3xl font-bold text-white">
                Our Vision
              </h2>
            </div>
            <p className="text-lg leading-relaxed text-white/90 max-w-4xl">
              Transforming society, enterprises, and institutions through the adoption of digital intelligent technologies for the purpose of propelling significant economic and social change.
            </p>
          </div>
        </motion.div>
      </section>

      {/* How We Work Section */}
      <section className={`py-10 ${theme === 'dark' ? 'bg-[#12131F]' : 'bg-gray-50'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid lg:grid-cols-2 gap-12 items-center"
          >
            <div>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-xl bg-[#00C8EB]/10 flex items-center justify-center">
                  <Handshake size={28} weight="duotone" className="text-[#00C8EB]" />
                </div>
                <h2 className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  How We Work
                </h2>
              </div>
              <p className={`text-lg leading-relaxed mb-6 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                We work remotely or onsite depending on the project. When necessary, we engage with clients 
                wherever they are located. This approach ensures continuous technical support.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
                  theme === 'dark' ? 'bg-[#0A0A10] border border-[#464B8B]/30' : 'bg-white shadow-sm'
                }`}>
                  <Globe size={20} weight="duotone" className="text-[#00C8EB]" />
                  <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>Remote Work</span>
                </div>
                <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
                  theme === 'dark' ? 'bg-[#0A0A10] border border-[#464B8B]/30' : 'bg-white shadow-sm'
                }`}>
                  <MapPin size={20} weight="duotone" className="text-[#00C8EB]" />
                  <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>Onsite Support</span>
                </div>
              </div>
            </div>
            <div className={`p-8 rounded-2xl ${
              theme === 'dark' ? 'bg-[#0A0A10] border border-[#464B8B]/20' : 'bg-white shadow-lg'
            }`}>
              <div className="grid grid-cols-2 gap-6 text-center">
                <div>
                  <div className="text-4xl font-bold text-[#00C8EB] mb-2">24/7</div>
                  <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Technical Support</p>
                </div>
                <div>
                  <div className="text-4xl font-bold text-[#464B8B] mb-2">100%</div>
                  <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Client Focused</p>
                </div>
                <div>
                  <div className="text-4xl font-bold text-[#00C8EB] mb-2">Global</div>
                  <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Reach</p>
                </div>
                <div>
                  <div className="text-4xl font-bold text-[#464B8B] mb-2">Agile</div>
                  <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Methodology</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Our Team Section */}
      <section className="py-10 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-xl bg-[#00C8EB]/10 flex items-center justify-center">
              <Users size={28} weight="duotone" className="text-[#00C8EB]" />
            </div>
          </div>
          <h2 className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Our Team
          </h2>
          <p className={`mt-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Meet the leadership driving innovation at ATLAS SOFTECH
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-md mx-auto"
        >
          <div className={`p-8 rounded-3xl text-center ${
            theme === 'dark' 
              ? 'bg-[#12131F] border border-[#464B8B]/20' 
              : 'bg-white shadow-lg'
          }`}>
            {/* CEO Avatar */}
            <div className="w-32 h-32 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#464B8B] to-[#00C8EB] p-1">
              <img 
                src="https://customer-assets.emergentagent.com/job_atlas-enterprise-hub/artifacts/vqlk1xi4_CEO_Picture_OG.png"
                alt="Mr Sunday Sokoni, CEO"
                className="w-full h-full rounded-full object-cover object-top"
              />
            </div>
            
            <h3 className={`text-2xl font-bold mb-1 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              Mr Sunday Sokoni, MBA
            </h3>
            <p className="text-[#00C8EB] font-medium mb-4">
              Chief Executive Officer
            </p>
            <p className={`mb-6 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              Business and Technology Expert
            </p>
            
            <div className="flex justify-center">
              <a 
                href="https://linkedin.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className={`p-3 rounded-full transition-colors ${
                  theme === 'dark' 
                    ? 'bg-[#0A0A10] hover:bg-[#464B8B]/30 text-gray-400 hover:text-[#00C8EB]' 
                    : 'bg-gray-100 hover:bg-[#00C8EB]/10 text-gray-600 hover:text-[#00C8EB]'
                }`}
              >
                <LinkedinLogo size={24} weight="duotone" />
              </a>
            </div>
          </div>
        </motion.div>
      </section>

      {/* CTA Section */}
      <section className={`py-16 ${theme === 'dark' ? 'bg-[#12131F]' : 'bg-gray-50'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className={`text-3xl md:text-4xl font-bold mb-6 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              Ready to Work Together?
            </h2>
            <p className={`text-lg mb-8 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              Let's discuss how ATLAS SOFTECH can help transform your business with intelligent technology solutions.
            </p>
            <Link to="/contact">
              <Button
                data-testid="about-cta-btn"
                size="lg"
                className="bg-[#00C8EB] hover:bg-[#00C8EB]/90 text-[#0A0A10] font-semibold px-8"
              >
                Get In Touch
                <ArrowRight size={20} className="ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
